import { StackdriverLogger } from '@klpkonsern/stackdriver-logger';
import type { FormValuesType } from '../../components/FormContainer';

type CalculateEditable = {
    isEditable: boolean;
    value: number;
};

const JS_GLOBALS = new Set([
    'true',
    'false',
    'null',
    'undefined',
    'NaN',
    'Infinity',
    'Math',
    'parseInt',
    'parseFloat',
    'isNaN',
    'isFinite',
]);

const HTML_ENTITIES: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
};

const decodeHtmlEntities = (str: string): string =>
    str.replace(/&amp;|&lt;|&gt;|&quot;|&#39;/g, (m) => HTML_ENTITIES[m]);

/**
 * Extracts form field identifiers from an expression, ignoring string literals and JS globals.
 */
const extractIdentifiers = (expression: string): Array<string> => {
    const withoutStrings = expression.replace(/'[^']*'|"[^"]*"/g, '');
    return (
        withoutStrings
            .match(/\b[a-zA-Z_]\w*\b/g)
            ?.filter((token) => !JS_GLOBALS.has(token)) ?? []
    );
};

// Strip Norwegian number formatting before substituting into JS expressions:
// space thousand separator → removed, comma decimal separator → dot
const stripNorwegianNumberFormat = (raw: string): string => {
    const stripped = raw.replace(/\s/g, '').replace(/,/g, '.');
    return stripped !== '' && !isNaN(Number(stripped)) ? stripped : raw;
};

/**
 * Extracts value from form field value, handling both simple strings and calculate editable objects
 */
const GetValueFromFormFieldValue = (value: string): string => {
    if (typeof value !== 'string') {
        return String(value ?? '');
    }
    try {
        const calculateEditable = JSON.parse(value) as CalculateEditable;
        if (
            typeof calculateEditable !== 'object' ||
            calculateEditable === null
        ) {
            throw new TypeError('Not a CalculateEditable object');
        }
        // Pass through stripNorwegianNumberFormat in case backend sends a
        // string-typed value (e.g. "1 500 000") rather than a JSON number.
        return stripNorwegianNumberFormat(
            calculateEditable.value?.toString() ?? '',
        );
    } catch {
        return stripNorwegianNumberFormat(value);
    }
};

/**
 * Evaluates JavaScript expressions with form values substituted
 */
export const evaluateExpression = <T>(
    expression: string,
    values: FormValuesType,
): T | undefined => {
    let modifiedExpression = decodeHtmlEntities(expression);
    Object.keys(values).forEach((key) => {
        const value = GetValueFromFormFieldValue(values[key]);
        const regex = new RegExp(`\\b${key}\\b`, 'g'); // Matches whole words (only)
        modifiedExpression = modifiedExpression.replace(
            regex,
            value === null || value === '' ? 'null' : value,
        );
    });

    const hasUnresolvedVars = extractIdentifiers(modifiedExpression).some(
        (id) => !(id in values),
    );

    if (hasUnresolvedVars) {
        return undefined;
    }

    // Define a function that will evaluate the expression
    try {
        const evalFunction = new Function(`return ${modifiedExpression};`);
        // Execute the function and return the result
        const result: T = evalFunction();
        return result;
    } catch (error) {
        if (error instanceof ReferenceError || error instanceof SyntaxError) {
            const structuredError = new Error(
                `Evaluation for expression '${expression}' failed`,
                {
                    cause: {
                        name: error.name,
                        originalMessage: error.message,
                        stack: error.stack,
                        errorType: 'EvaluateExpression error',
                        expression,
                        evaluatedExpression: modifiedExpression,
                    },
                },
            );
            StackdriverLogger.log(structuredError);
        } else {
            const msg =
                `Evaluation for expression '${expression}' failed with exception: ` +
                error;
            StackdriverLogger.log(msg);
        }

        return undefined;
    }
};

/**
 * Checks if an expression contains any empty operands
 */
export const expressionContainsEmptyOperand = (
    values: FormValuesType,
    expression?: string,
): boolean => {
    if (!expression) return false;
    return extractIdentifiers(expression).some(
        (key) => !values[key] || values[key] === null || values[key] === '',
    );
};
